import { LOCALES, LOCALE_META, type Locale } from '@/lib/locale';
import { useLocale } from '@/lib/locale-context';
import { copyFor } from '@/lib/messages';
import { FlagMark, flagOptionHtml } from './flag-marks';

/**
 * A native `<select>` wearing a custom face.
 *
 * The transparent select sits on top of the visible flag + chevron and does
 * ALL the work: keyboard support, form semantics, screen-reader semantics —
 * none of which a hand-rolled listbox gets right for free.
 *
 * The dropdown is progressively enhanced. Where the customizable-select API
 * exists (`appearance: base-select` — Chromium, Android included), the
 * picker is ours and the options render as SVG flag + language name
 * (`.language-select` styles in globals.css); the select semantics are
 * untouched, which is the point of that API. Everywhere else the OS picker
 * appears and shows the names alone — the SVG sits in the option's DOM
 * either way, but a native picker paints only the option's text. Each name
 * is in its own language, so a German speaker lost on an English screen can
 * still find "Deutsch".
 */
export function LanguageSelector() {
  const { locale, setLocale } = useLocale();
  const copy = copyFor(locale);

  return (
    <span className="relative inline-flex items-center gap-1.5 rounded px-2 py-2 text-sm text-buds-muted focus-within:outline focus-within:outline-2 focus-within:outline-buds-dark">
      <span
        aria-hidden="true"
        className="inline-flex overflow-hidden rounded-[2px] ring-1 ring-buds-dark/10"
      >
        <FlagMark locale={locale} className="block h-3 w-4" />
      </span>
      <svg
        aria-hidden="true"
        viewBox="0 0 12 12"
        className="size-3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      >
        <path d="M2.5 4.5 6 8l3.5-3.5" />
      </svg>
      <select
        aria-label={copy.languageSelector}
        value={locale}
        onChange={(event) => setLocale(event.target.value as Locale)}
        className="language-select absolute inset-0 cursor-pointer opacity-0"
      >
        {LOCALES.map((value) => (
          // innerHTML rather than children: see flag-marks.tsx — React's
          // nesting validator still rejects element children of <option>.
          <option
            key={value}
            value={value}
            dangerouslySetInnerHTML={{
              __html: flagOptionHtml(value, LOCALE_META[value].label),
            }}
          />
        ))}
      </select>
    </span>
  );
}
